/**
 * Service for duration calculation operations
 */

import { DateValidationService } from './DateValidationService';
import { DateStorageService } from './DateStorageService';

export class DurationCalculationService {
  /**
   * Calculate duration between two dates in days
   */
  static calculateDuration(startDate: Date, endDate: Date): number {
    // Check if same day
    if (DateValidationService.isSameDay(startDate, endDate)) {
      return 1;
    }

    // Calculate difference using date-only objects to avoid time issues
    const startDateOnly = new Date(
      startDate.getFullYear(), 
      startDate.getMonth(), 
      startDate.getDate()
    );
    const endDateOnly = new Date(
      endDate.getFullYear(), 
      endDate.getMonth(), 
      endDate.getDate()
    );

    const diffTime = Math.abs(endDateOnly.getTime() - startDateOnly.getTime());
    const days = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

    return days;
  }

  /**
   * Calculate and save duration, then trigger onChange callback
   */
  static processDateSelectionComplete(
    startDate: Date, 
    endDate: Date, 
    onChange: (value: number[]) => void
  ): number {
    const duration = this.calculateDuration(startDate, endDate);
    
    // Save duration to storage
    DateStorageService.saveDuration(duration);
    
    // Trigger onChange callback
    onChange([duration]);
    
    console.log(`Date selection complete: ${endDate.toISOString()}, duration: ${duration} days`);
    
    return duration;
  }

  /**
   * Generate end date from start date and duration
   */
  static generateEndDateFromDuration(startDate: Date, durationDays: number): Date {
    const endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + durationDays - 1);
    endDate.setHours(23, 59, 59, 999);
    return endDate;
  }

  /**
   * Seed dates when duration is set but no dates exist
   */
  static seedDatesForDuration(durationDays: number): { startDate: Date; endDate: Date } {
    const minDate = DateValidationService.getMinimumDate();
    const startDate = new Date(minDate);
    startDate.setHours(12, 0, 0, 0);
    
    const endDate = this.generateEndDateFromDuration(startDate, durationDays);
    
    return { startDate, endDate };
  }

  /**
   * Adjust end date to match specified duration
   */
  static adjustEndDateForDuration(
    startDate: Date, 
    currentEndDate: Date, 
    targetDuration: number
  ): { needsAdjustment: boolean; newEndDate?: Date } {
    const currentDuration = this.calculateDuration(startDate, currentEndDate);
    
    if (currentDuration === targetDuration) {
      return { needsAdjustment: false };
    }

    const newEndDate = this.generateEndDateFromDuration(startDate, targetDuration);
    return { needsAdjustment: true, newEndDate };
  }

  /**
   * Calculate duration from an array of specific dates
   * Each selected date counts as 1 day
   */
  static calculateDurationFromDates(dates: Date[]): number {
    return dates.length;
  }

  /**
   * Format dates array for display (groups consecutive dates)
   */
  static formatDateGroups(dates: Date[]): string[] {
    if (dates.length === 0) return [];
    
    const sorted = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const groups: string[] = [];
    let groupStart = sorted[0];
    let groupEnd = sorted[0];
    
    for (let i = 1; i <= sorted.length; i++) {
      const current = sorted[i];
      const prev = sorted[i - 1];
      
      if (current && this.isConsecutiveDay(prev, current)) {
        groupEnd = current;
      } else {
        // End current group
        if (groupStart.getTime() === groupEnd.getTime()) {
          groups.push(this.formatSingleDate(groupStart));
        } else {
          groups.push(`${this.formatSingleDate(groupStart)} - ${this.formatSingleDate(groupEnd)}`);
        }
        
        if (current) {
          groupStart = current;
          groupEnd = current;
        }
      }
    }
    
    return groups;
  }

  /**
   * Check if two dates are consecutive days
   */
  private static isConsecutiveDay(date1: Date, date2: Date): boolean {
    const diff = date2.getTime() - date1.getTime();
    const oneDayMs = 24 * 60 * 60 * 1000;
    return Math.abs(diff - oneDayMs) < 1000; // Allow 1 second tolerance
  }

  /**
   * Format a single date for display
   */
  private static formatSingleDate(date: Date): string {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  }
}